import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaStar, FaFilm, FaTv } from 'react-icons/fa';
import axios from 'axios';
import './PublicUserProfile.css';
import AppDownloadBanner from './AppDownloadBanner';

function PublicUserProfile() {
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        // Appel API public pour récupérer uniquement les infos publiques
        const response = await axios.get(`/api/get-profile?user_id=${userId}`, {
          // Pas withCredentials pour éviter les erreurs d'auth
          withCredentials: false
        });
        setUserData(response.data);
      } catch (error) {
        console.error('Error fetching public profile:', error);
        setError('Profil non trouvé ou privé');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserProfile();
    }
  }, [userId]);

  // Fonctions simples de détection de plateforme
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = () => /Android/.test(navigator.userAgent);
  const isDesktop = () => !isIOS() && !isAndroid();

  // Rendu du bouton selon la plateforme
  const renderAppButton = () => {
    if (isIOS() || isAndroid()) {
      return (
        <a href={`cinemapp://user/${userId}`} className="prominent-app-button">
          📱 Ouvrir dans CinéList
        </a>
      );
    } else if (isDesktop()) {
      return (
        <button 
          className="prominent-app-button desktop-download" 
          onClick={() => {
            alert('📱 Téléchargez CinéList sur votre mobile !\n\n🍎 iOS: App Store\n🤖 Android: Play Store');
          }}
        >
          📲 Télécharger CinéList
        </button>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-container">
          <h2>Chargement du profil...</h2>
        </div>
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="profile-page">
        <AppDownloadBanner userId={userId} />
        <div className="error-container">
          <h2>Profil non disponible</h2>
          <p>{error || 'Ce profil n\'est pas accessible publiquement.'}</p>
          <div className="download-suggestion">
            <h3>Voir ce profil dans l'app CinéList</h3>
            <p>Téléchargez l'app pour accéder aux profils complets et interagir avec la communauté !</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Helmet>
        <meta 
          name="apple-itunes-app" 
          content={`app-id=6740208004, app-argument=https://films.ahkapp.fr/user/${userId}`} 
        />
        <meta 
          property="og:title" 
          content={userData ? `${userData.pseudo} - CinéList` : 'Profil CinéList'} 
        />
        <meta 
          property="og:description" 
          content={userData ? `Découvrez le profil de ${userData.pseudo} sur CinéList` : 'Découvrez ce profil sur CinéList'} 
        />
        <meta 
          property="og:image" 
          content={userData && userData.avatar_url 
            ? userData.avatar_url 
            : 'https://films.ahkapp.fr/logo512.png'
          } 
        />
        <meta 
          property="og:image:width" 
          content="400" 
        />
        <meta 
          property="og:image:height" 
          content="400" 
        />
        <meta 
          property="og:image:type" 
          content="image/jpeg" 
        />
        <meta 
          property="og:url" 
          content={`https://films.ahkapp.fr/user/${userId}`} 
        />
        <meta 
          property="og:type" 
          content="profile" 
        />
      </Helmet>
      
      <AppDownloadBanner userId={userId} />
      
      <div className="public-profile-header">
        <div className="profile-avatar-section">
          <img 
            src={userData.avatar_url || '/logo.png'} 
            alt="Profile" 
            className="profile-avatar"
          />
          <h2>{userData.pseudo || 'Utilisateur CinéList'}</h2>
        </div>

        {/* Section bouton central proéminent */}
        <div className="app-button-section">
          {renderAppButton()}
        </div>

        <div className="profile-stats">
          <div className="stat-item">
            <FaFilm className="stat-icon" />
            <span className="stat-value">{userData.rated_movie_count || 0}</span>
            <span className="stat-label">Films notés</span>
          </div>
          <div className="stat-item">
            <FaTv className="stat-icon" />
            <span className="stat-value">{userData.rated_tv_count || 0}</span>
            <span className="stat-label">Séries notées</span>
          </div>
          <div className="stat-item">
            <FaStar className="stat-icon" />
            <span className="stat-value">{userData.badges_number || 0}</span>
            <span className="stat-label">Badges</span>
          </div>
        </div>

        {userData.bio && (
          <div className="profile-bio">
            <h3>À propos</h3>
            <p>{userData.bio}</p>
          </div>
        )}
      </div>

      <div className="app-promotion-section">
        <div className="app-promo-card">
          <h3>🎬 Rejoignez {userData.pseudo} sur CinéList !</h3>
          <div className="promo-features">
            <div className="promo-feature">
              <FaStar /> Notez et découvrez des films
            </div>
            <div className="promo-feature">
              <FaFilm /> Créez vos listes personnalisées
            </div>
            <div className="promo-feature">
              <FaTv /> Suivez vos amis et leurs goûts
            </div>
          </div>
          <p>
            Téléchargez l'app CinéList pour voir le profil complet de {userData.pseudo}, 
            ses recommandations et interagir avec la communauté !
          </p>
        </div>
      </div>

      <div className="limited-access-notice">
        <h4>Accès limité</h4>
        <p>
          Cette version web ne montre qu'un aperçu du profil. 
          Pour accéder à toutes les fonctionnalités (notes détaillées, compatibilité, 
          recommandations personnalisées), téléchargez l'app mobile CinéList.
        </p>
      </div>
    </div>
  );
}

export default PublicUserProfile;